import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Save, Upload, Link as LinkIcon, Mail, Phone, MapPin, Globe, Facebook, Twitter, Instagram, Linkedin, Youtube } from 'lucide-react';
import toast from 'react-hot-toast';
import {api} from '../../api';
import { AxiosError } from 'axios';


interface SiteSettings {
  _id?: string;
  siteName: string;
  logoUrl?: string;
  faviconUrl?: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  socialLinks: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
  };
  metaTags: {
    title: string;
    description: string;
    keywords: string;
  };
  footerText: string;
  copyrightText: string;
}

const AdminSiteSettings: React.FC = () => {
    const [settings, setSettings] = useState<SiteSettings>({
    siteName: 'FORMACADEMY',
    contactEmail: '',
    contactPhone: '',
    address: '',
    socialLinks: {},
    metaTags: {
      title: '',
      description: '',
      keywords: ''
    },
    footerText: '',
    copyrightText: ''
  });
  
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [faviconPreview, setFaviconPreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'contact' | 'social' | 'seo'>('general');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
  try {
    setIsLoading(true);
    const response = await api.get('/admin/settings');
    const data = response.data;
    
    // Ensure metaTags exists in the response
    const settingsData = {
      ...data.settings,
      metaTags: data.settings.metaTags || {
        title: '',
        description: '',
        keywords: ''
      },
      socialLinks: data.settings.socialLinks || {}
    };
    
    setSettings(settingsData);
    
    if (settingsData.logoUrl) {
      setLogoPreview(settingsData.logoUrl);
    }
    
    if (settingsData.faviconUrl) {
      setFaviconPreview(settingsData.faviconUrl);
    }
  } catch (error) {
    console.error('Load settings error:', error);
    toast.error((error as AxiosError<{ message?: string }>)?.response?.data?.message || 'Erreur lors du chargement des paramètres');
  } finally {
    setIsLoading(false);
  }
};

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Gérer les champs imbriqués
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setSettings(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof SiteSettings] as unknown as Record<string, unknown>),
          [child]: value
        }
      }));
    } else {
      setSettings(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      
      // Créer un aperçu
      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFaviconFile(file);
      
      // Créer un aperçu
      const reader = new FileReader();
      reader.onload = () => {
        setFaviconPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      
      const formData = new FormData();
      
      // Ajouter les champs simples
      formData.append('siteName', settings.siteName);
      formData.append('contactEmail', settings.contactEmail);
      formData.append('contactPhone', settings.contactPhone);
      formData.append('address', settings.address);
      formData.append('footerText', settings.footerText);
      formData.append('copyrightText', settings.copyrightText);
      
      // Ajouter les objets
      formData.append('socialLinks', JSON.stringify(settings.socialLinks));
      formData.append('metaTags', JSON.stringify(settings.metaTags));
      
      // Ajouter les fichiers
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      
      if (faviconFile) {
        formData.append('favicon', faviconFile);
      }
      
      await api.put('/admin/settings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Paramètres mis à jour avec succès');
      loadSettings(); // Recharger les paramètres
    } catch (error) {
      console.error('Save settings error:', error);
      toast.error((error as AxiosError<{ message?: string }>)?.response?.data?.message || 'Erreur lors de la sauvegarde des paramètres');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB639]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Paramètres du Site</h1>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex items-center px-6 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Sauvegarde...' : 'Enregistrer'}
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('general')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'general'
                  ? 'border-[#FFB639] text-[#FFB639]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Général
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'contact'
                  ? 'border-[#FFB639] text-[#FFB639]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Contact
            </button>
            <button
              onClick={() => setActiveTab('social')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'social'
                  ? 'border-[#FFB639] text-[#FFB639]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Réseaux sociaux
            </button>
            <button
              onClick={() => setActiveTab('seo')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'seo'
                  ? 'border-[#FFB639] text-[#FFB639]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              SEO
            </button>
          </nav>
        </div>

        {/* Settings Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du site
                  </label>
                  <input
                    type="text"
                    name="siteName"
                    value={settings.siteName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        <input
                          type="file"
                          id="logo"
                          onChange={handleLogoChange}
                          className="hidden"
                          accept="image/*"
                        />
                        <label
                          htmlFor="logo"
                          className="cursor-pointer flex flex-col items-center justify-center"
                        >
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">
                            Cliquez pour uploader un logo
                          </span>
                        </label>
                      </div>
                    </div>
                    {logoPreview && (
                      <div className="w-24 h-24 border border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-white">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Favicon
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        <input
                          type="file"
                          id="favicon"
                          onChange={handleFaviconChange}
                          className="hidden"
                          accept="image/x-icon,image/png,image/svg+xml"
                        />
                        <label
                          htmlFor="favicon"
                          className="cursor-pointer flex flex-col items-center justify-center"
                        >
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">
                            Cliquez pour uploader un favicon
                          </span>
                        </label>
                      </div>
                    </div>
                    {faviconPreview && (
                      <div className="w-12 h-12 border border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-white">
                        <img
                          src={faviconPreview}
                          alt="Favicon preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Texte du pied de page
                  </label>
                  <textarea
                    name="footerText"
                    value={settings.footerText}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Texte de copyright
                  </label>
                  <input
                    type="text"
                    name="copyrightText"
                    value={settings.copyrightText}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                </div>
              </div>
            )}
            
            {/* Contact Settings */}
            {activeTab === 'contact' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Email de contact
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={settings.contactEmail}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="inline h-4 w-4 mr-1" />
                    Téléphone de contact
                  </label>
                  <input
                    type="text"
                    name="contactPhone"
                    value={settings.contactPhone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <MapPin className="inline h-4 w-4 mr-1" />
                    Adresse
                  </label>
                  <textarea
                    name="address"
                    value={settings.address}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                </div>
              </div>
            )}
            
            {/* Social Media Settings */}
            {activeTab === 'social' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Facebook className="inline h-4 w-4 mr-1 text-[#FFB639]" />
                    Facebook
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      <LinkIcon className="h-4 w-4" />
                    </span>
                    <input
                      type="url"
                      name="socialLinks.facebook"
                      value={settings.socialLinks.facebook || ''}
                      onChange={handleInputChange}
                      placeholder="https://facebook.com/votrepage"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Twitter className="inline h-4 w-4 mr-1 text-[#FFB639]" />
                    Twitter
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      <LinkIcon className="h-4 w-4" />
                    </span>
                    <input
                      type="url"
                      name="socialLinks.twitter"
                      value={settings.socialLinks.twitter || ''}
                      onChange={handleInputChange}
                      placeholder="https://twitter.com/votrecompte"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Instagram className="inline h-4 w-4 mr-1 text-[#FFB639]" />
                    Instagram
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      <LinkIcon className="h-4 w-4" />
                    </span>
                    <input
                      type="url"
                      name="socialLinks.instagram"
                      value={settings.socialLinks.instagram || ''}
                      onChange={handleInputChange}
                      placeholder="https://instagram.com/votrecompte"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Linkedin className="inline h-4 w-4 mr-1 text-[#e5a333]" />
                    LinkedIn
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      <LinkIcon className="h-4 w-4" />
                    </span>
                    <input
                      type="url"
                      name="socialLinks.linkedin"
                      value={settings.socialLinks.linkedin || ''}
                      onChange={handleInputChange}
                      placeholder="https://linkedin.com/company/votreentreprise"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Youtube className="inline h-4 w-4 mr-1 text-red-600" />
                    YouTube
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      <LinkIcon className="h-4 w-4" />
                    </span>
                    <input
                      type="url"
                      name="socialLinks.youtube"
                      value={settings.socialLinks.youtube || ''}
                      onChange={handleInputChange}
                      placeholder="https://youtube.com/c/votrechaine"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* SEO Settings */}
            {activeTab === 'seo' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Globe className="inline h-4 w-4 mr-1" />
                    Titre du site (SEO)
                  </label>
                  <input
                    type="text"
                    name="metaTags.title"
                    value={settings.metaTags.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Titre qui apparaîtra dans les résultats de recherche
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (SEO)
                  </label>
                  <textarea
                    name="metaTags.description"
                    value={settings.metaTags.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Description qui apparaîtra dans les résultats de recherche (150-160 caractères recommandés)
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mots-clés (SEO)
                  </label>
                  <input
                    type="text"
                    name="metaTags.keywords"
                    value={settings.metaTags.keywords}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Mots-clés séparés par des virgules
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AdminSiteSettings;